import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

// comps
import EmailVerification from "./components/EmailVerification"
import EnabledMethods from "./components/EnabledMethods"
import Sms from "./components/Sms"
import Totp from "./components/Totp"

import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"
import { LogError, LogInfo } from "../../../helpers/logger"
import {
  clearSessStorage,
  getSessStorage,
  setSessStorage,
} from "../../../helpers/sessionStorage"
import { useRecaptcha } from "../../../hooks/captcha/useRecaptcha"
import { useAuth } from "../../../context/authContext"

const DEFAULT_FINALIZE_STATE = { pending: false, error: "" }

const getFinalizeStorageKey = (uid, factorId) =>
  `mfa-finalize:${uid}:${factorId}`

const readFinalizeStateFromStorage = (uid, factorId) => {
  if (!uid) return DEFAULT_FINALIZE_STATE

  const rawValue = getSessStorage(getFinalizeStorageKey(uid, factorId))
  if (!rawValue) return DEFAULT_FINALIZE_STATE

  try {
    const parsed = JSON.parse(rawValue)
    if (parsed?.pending) {
      return {
        pending: true,
        error: parsed.error || "",
      }
    }
  } catch {
    // Ignore malformed persisted state and reset it below.
  }

  clearSessStorage(getFinalizeStorageKey(uid, factorId))
  return DEFAULT_FINALIZE_STATE
}

const persistFinalizeState = (uid, factorId, nextState) => {
  if (!uid) return

  const storageKey = getFinalizeStorageKey(uid, factorId)
  if (!nextState?.pending) {
    clearSessStorage(storageKey)
    return
  }

  setSessStorage(storageKey, JSON.stringify(nextState))
}

export default function TwoFASettings() {
  const [current2FAMethod, setCurrent2FAMethod] = useState(null)
  const [mfaFinalizeState, setMfaFinalizeState] = useState({
    phone: { pending: false, error: "" },
    totp: { pending: false, error: "" },
  })

  const { auth, authUser, reloadAuthUser, getAuthIdToken } = useAuth()
  const {
    verifier: recaptchaVerifier,
    error: recaptchaInitError,
    isReady: recaptchaReady,
  } = useRecaptcha(auth)

  const location = useLocation()

  const emailVerified = !!authUser?.emailVerified
  const enrolledFactors = authUser?.multiFactor?.enrolledFactors || []

  useEffect(() => {
    const uid = authUser?.uid
    if (!uid) {
      setMfaFinalizeState({
        phone: DEFAULT_FINALIZE_STATE,
        totp: DEFAULT_FINALIZE_STATE,
      })
      return
    }

    setMfaFinalizeState({
      phone: readFinalizeStateFromStorage(uid, "phone"),
      totp: readFinalizeStateFromStorage(uid, "totp"),
    })
  }, [authUser?.uid])

  useEffect(() => {
    // Check if the 'tab' query parameter exists in the URL
    const searchParams = new URLSearchParams(location.search)
    const tabParam = searchParams.get("tab")

    // Set the 'tab' state based on the query parameter
    if (tabParam) {
      setCurrent2FAMethod(tabParam)
    }
  }, [location.search])

  async function reloadUser() {
    const newAuthUser = await reloadAuthUser()
    if (!newAuthUser) return

    LogInfo("User reloaded:", newAuthUser)
  }

  async function setEnableMfaEnrolledFactor(factorId) {
    const currentUser = auth.currentUser || authUser

    try {
      if (!currentUser) {
        throw new Error("User session is unavailable. Please sign in again.")
      }

      // Persist pending state before async work so refresh/reopen can recover.
      const inProgressState = { pending: true, error: "" }
      persistFinalizeState(currentUser.uid, factorId, inProgressState)
      setMfaFinalizeState((current) => ({
        ...current,
        [factorId]: inProgressState,
      }))

      const userToken = await getAuthIdToken(currentUser)
      if (!userToken) {
        throw new Error("Unable to refresh your session. Please sign in again.")
      }

      await callCloudFunction(
        "enableMfaEnrolledFactor",
        { uid: currentUser.uid, preferred_method: factorId },
        userToken
      )

      const clearedState = { pending: false, error: "" }
      persistFinalizeState(currentUser.uid, factorId, clearedState)

      setMfaFinalizeState((current) => ({
        ...current,
        [factorId]: clearedState,
      }))

      return true
    } catch (error) {
      LogError("Error enrolling user:", error)

      const fallbackMessage =
        "Two-factor authentication was enabled, but we could not finish account setup."
      const backendErrorMessage =
        error?.response?.data?.message || error?.message || fallbackMessage

      const pendingState = {
        pending: true,
        error: backendErrorMessage,
      }
      persistFinalizeState(currentUser?.uid || authUser?.uid, factorId, pendingState)

      setMfaFinalizeState((current) => ({
        ...current,
        [factorId]: pendingState,
      }))

      throw error
    }
  }

  const handleConfigNewMethod = () => {
    if (current2FAMethod === null) return null

    const handleEnrolledFactors = (id) =>
      enrolledFactors.find(({ factorId }) => factorId === id)

    return (
      <div>
        <h2>Step 3: Setting Up the 2FA Method</h2>
        {current2FAMethod === "sms" && (
          <Sms
            reloadUser={reloadUser}
            setEnableMfaEnrolledFactor={setEnableMfaEnrolledFactor}
            enrolledFactor={handleEnrolledFactors("phone")}
            finalizationState={mfaFinalizeState.phone}
            recaptchaVerifier={recaptchaVerifier}
            recaptchaInitError={recaptchaInitError}
            recaptchaReady={recaptchaReady}
          />
        )}
        {current2FAMethod === "totp" && (
          <Totp
            reloadUser={reloadUser}
            setEnableMfaEnrolledFactor={setEnableMfaEnrolledFactor}
            enrolledFactor={handleEnrolledFactors("totp")}
            finalizationState={mfaFinalizeState.totp}
          />
        )}
      </div>
    )
  }

  if (!authUser) return null

  return (
    <div className="main-content">
      <h1>Configuring Two-Factor Authentication for Your Account</h1>
      <EmailVerification
        emailVerified={emailVerified}
        reloadUser={reloadUser}
      />
      <EnabledMethods
        current2FAMethod={current2FAMethod}
        enrolledFactors={enrolledFactors}
        setCurrent2FAMethod={setCurrent2FAMethod}
      />
      {handleConfigNewMethod()}
    </div>
  )
}
